import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardHeader,
  CardContent,
  CardFooter,
} from '@/components/ui/card';
import { Heading } from '@/components/ui/shared/heading';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogBody,
} from '@/components/ui/dialog';
import { useHistory } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import useFetch from 'use-http';
import { useUserState } from '@/components/context/UserContext';
import { Calendar, Ban, Loader2 } from 'lucide-react';

const PAYMENT_TYPES_WITHOUT_PRICE = new Set([
  'Kita',
  'Barteris',
  'Be sutarties',
  'Partneris',
]);

export const VisitServiceDetails = ({ visitDetails = {} }) => {
  const {
    post: cancelRegistrationRequestSend,
    response: cancelRegistrationResponse,
  } = useFetch(`/RegistrationManagement/Registrations/CancelPsdf`);

  const history = useHistory();
  const [showCancelRegistrationModal, setShowCancelRegistrationModal] =
    useState(false);
  const [showResultModalTextContent, setShowResultModalTextContent] =
    useState('');
  const [
    showCancelRegistrationResultModal,
    setShowCancelRegistrationResultModal,
  ] = useState(false);

  const userState = useUserState();
  const [loading, setLoading] = useState(false);

  const { t } = useTranslation();

  const [isPsdf, setIsPsdf] = useState(
    visitDetails.services?.length > 0 &&
      visitDetails.services.every((service) => {
        return (
          service.payments?.length > 0 &&
          service.payments.every(
            (payment) => payment.paymentType === 'VLK (PSDF)',
          )
        );
      }),
  );

  const isUnpaid =
    Array.isArray(visitDetails.services) &&
    visitDetails.services.length > 0 &&
    visitDetails.services.every(
      (service) =>
        Array.isArray(service.payments) &&
        service.payments.length > 0 &&
        service.payments.every(
          (payment) =>
            payment?.status === 'Neapmokėta' ||
            payment?.status === null ||
            payment?.paymentType === 'Draudimas' ||
            payment?.paymentType === 'Partneris',
        ),
    );

  const formatDateWithoutConversion = (date) => {
    if (!date) {
      return '-';
    }

    const value = String(date);
    const datePart = value.substring(0, 10);
    const timePart = value.substring(11, 16);

    if (!datePart) {
      return '-';
    }

    if (!timePart || timePart === '00:00') {
      return datePart;
    }

    return `${datePart} ${timePart}`;
  };

  const getDateForComparisonWithoutTimezone = (date) => {
    if (!date) {
      return null;
    }

    const value = String(date);
    const withoutTimezone = value
      .replace(/Z$/, '')
      .replace(/[+-]\d{2}:\d{2}$/, '');

    const parsed = new Date(withoutTimezone);

    if (Number.isNaN(parsed.getTime())) {
      return null;
    }

    return parsed;
  };

  const ONE_DAY_MS = 24 * 60 * 60 * 1000;
  const visitDateForComparison = getDateForComparisonWithoutTimezone(
    visitDetails?.date,
  );
  const visitTime = visitDateForComparison?.getTime();

  const isCancelableByTime =
    !visitDetails?.date || Date.now() <= visitTime - ONE_DAY_MS;

  const isVisitTimePassed = !!visitTime && Date.now() > visitTime;

  const renderMessageWithEmail = (text) => {
    if (!text) return null;

    const emailRegex = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;
    const parts = text.split(emailRegex);

    return parts.map((part, index) => {
      const isEmail = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(
        part,
      );

      if (isEmail) {
        return (
          <a
            key={index}
            href={`mailto:${part}`}
            className="inline text-blue-600 underline"
          >
            {part}
          </a>
        );
      }

      return <React.Fragment key={index}>{part}</React.Fragment>;
    });
  };

  const handleSubmit = async (visitId) => {
    setLoading(true);

    const response = await cancelRegistrationRequestSend('', {
      visitId: visitId,
      espbiEsi: userState.espbiEsi,
    });

    if (response.status >= 400) {
      setShowResultModalTextContent(
        t('userAccount.registrationsTab.registrationFailedCancelledSecondary'),
      );
      setShowCancelRegistrationResultModal(true);
      setShowCancelRegistrationModal(false);
    } else {
      if (!response.registrationIsCancelled) {
        if (response.message) {
          setShowResultModalTextContent(response.message);
        } else {
          setShowResultModalTextContent(
            t('userAccount.registrationsTab.cancelRegistrationFailed'),
          );
        }

        setShowCancelRegistrationResultModal(true);
        setShowCancelRegistrationModal(false);
      } else {
        setShowResultModalTextContent(
          t('userAccount.registrationsTab.cancelRegistrationSuccess'),
        );
        setShowCancelRegistrationResultModal(true);
        setShowCancelRegistrationModal(false);
      }
    }

    setLoading(false);
  };

  const {
    get: calendarInviteDownload,
    response: calendarInviteDownloadResponse,
  } = useFetch('UserAccount/createCalendar');

  function calculateRemainingPaymentPrice(service) {
    if (
      Array.isArray(service.payments) &&
      service.payments.some((x) => x.paymentType !== null)
    ) {
      let price = service.price;

      service.payments.forEach((payment) => {
        if (payment.paymentType != null) {
          price -= payment.price;
        }
      });

      price = Math.round(price * 100) / 100;

      return price > 0 ? price.toFixed(2) : null;
    }

    return null;
  }

  const downloadCalendarInvite = async () => {
    await calendarInviteDownload(`?id=${visitDetails.visitId}`).then((res) => {
      const a = document.createElement('a');
      a.href = res;
      a.download = 'Affidea_Registracija.ics';

      setTimeout(() => {
        a.click();
      }, 120);
    });
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <Heading variant="userAccountDetailsTitle">
            {t('userAccount.visitTab.visitInfo')}
          </Heading>
        </CardHeader>

        <CardContent className="space-y-2">
          <div>
            <span>{t('userAccount.visitTab.medicalCenter')} </span>
            <span>
              {visitDetails.department} {visitDetails.departmentAddress}
            </span>
          </div>

          <div>
            <span>{t('userAccount.visitTab.date')}</span>
            <span>{formatDateWithoutConversion(visitDetails.date)}</span>
          </div>

          {(isPsdf || (isUnpaid && isCancelableByTime)) &&
            !isVisitTimePassed && (
              <div>
                <Button
                  variant="light"
                  onClick={() => {
                    setShowCancelRegistrationModal(true);
                  }}
                >
                  <Ban />
                  Atšaukti registraciją
                </Button>
              </div>
            )}

          {!isVisitTimePassed && (
            <div>
              <Button variant="light" onClick={downloadCalendarInvite}>
                <Calendar />
                Pridėti kalendoriuje
              </Button>
            </div>
          )}
        </CardContent>

        <CardFooter />
      </Card>

      {Array.isArray(visitDetails.services) &&
        visitDetails.services.map((service, index) => (
          <Card key={index}>
            <CardHeader>
              <Heading variant="userAccountDetailsTitle">
                {service.name}
              </Heading>
            </CardHeader>

            <CardContent className="space-y-2">
              {Array.isArray(service.payments) &&
                service.payments.length > 0 && (
                  <div>
                    <span>{t('userAccount.visitTab.price')} </span>
                    <span>
                      {service.payments.some(
                        (x) =>
                          x?.paymentType === 'VLK (PSDF)' ||
                          PAYMENT_TYPES_WITHOUT_PRICE.has(x?.paymentType),
                      )
                        ? '-'
                        : service.price.toFixed(2)}
                    </span>
                  </div>
                )}

              {Array.isArray(service.payments) &&
                service.payments.some(
                  (x) =>
                    x?.paymentType?.toLowerCase() !== 'draudimas' &&
                    x?.paymentType !== 'VLK (PSDF)',
                ) &&
                service.status && (
                  <div>
                    <span>{t('userAccount.visitTab.state')} </span>
                    <span>
                      {service.status} {calculateRemainingPaymentPrice(service)}
                    </span>
                  </div>
                )}

              {Array.isArray(service.payments) &&
                service.payments.some((x) => x?.paymentType !== null) && (
                  <span>
                    <span>{t('userAccount.visitTab.paymentType')} </span>
                  </span>
                )}

              {Array.isArray(service.payments) &&
                service.payments
                  .filter((x) => x?.paymentType !== null)
                  .map((payment, i) => (
                    <span key={i}>
                      <span>{payment.paymentType}</span>
                      {payment.paymentType !== 'VLK (PSDF)' && (
                        <span>
                          {' -'}
                          {!PAYMENT_TYPES_WITHOUT_PRICE.has(
                            payment.paymentType,
                          ) && ` ${payment.price.toFixed(2)} `}
                        </span>
                      )}
                    </span>
                  ))}
            </CardContent>

            <CardFooter />
          </Card>
        ))}

      <Dialog
        open={showCancelRegistrationModal}
        onOpenChange={() => setShowCancelRegistrationModal(false)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Registracijos atšaukimas</DialogTitle>
          </DialogHeader>

          <DialogBody>Ar tikrai norite atšaukti registraciją?</DialogBody>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowCancelRegistrationModal(false);
              }}
            >
              Grįžti
            </Button>

            <Button
              variant="secondary"
              onClick={() => {
                handleSubmit(visitDetails.visitId);
              }}
            >
              {loading && <Loader2 className="h-5 w-5 animate-spin " />}
              Taip
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showCancelRegistrationResultModal}
        onOpenChange={() => setShowCancelRegistrationResultModal(false)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Registracijos atšaukimas</DialogTitle>
          </DialogHeader>

          <DialogBody className="inline">
            {renderMessageWithEmail(showResultModalTextContent)}
          </DialogBody>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowCancelRegistrationResultModal(false);
                history.push('/userAccount/visits');
              }}
            >
              Gerai
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
